import { format, differenceInHours, isAfter, isBefore, isToday, isTomorrow, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutePath } from '../../../layout/Routes'
import styles from '../MyTasksContainer.module.scss'

import { DeadlineBadgeProps, MyTasksTableProps } from './MyTasksTable.types'
import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import {
  Badge,
  FlagAvatar,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'

export function MyTasksTable({
  data,
  isLoading,
  previousPage,
  nextPage,
  totalResults,
  sortDirection,
  handleRequestSort,
  sortByColumn,
  filters
}: Readonly<MyTasksTableProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columnsTasks: TableColumn[] = [
    { id: 'country', content: t('table.header.country'), isSortable: false, props: { width: '10%' } },
    { id: 'organization', content: t('table.header.organization'), isSortable: false, props: { width: '30%' } },
    { id: 'program', content: t('table.header.program'), isSortable: false, props: { width: '35%' } },
    { id: 'type', content: t('table.header.type'), isSortable: false, props: { width: '15%' } },
    { id: 'deadline', content: t('table.header.deadline'), isSortable: true, props: { width: '10%' } }
  ]

  const navigateToTask = (programId: string, programName: string, candidateId: string, formId?: string) => {
    const searchParams = new URLSearchParams({ tasksView: 'true' })

    if (formId) {
      searchParams.set('formId', formId)
    }
    navigate(
      {
        pathname: `${RoutePath.GRANT_PROGRAMS}/${programId}/candidates/${candidateId}`,
        search: `?${searchParams.toString()}`
      },
      {
        state: {
          organisationName: programName
        }
      }
    )
  }

  const getDeadlineBadgeProps = (deadlineStr: string | null): DeadlineBadgeProps => {
    if (!deadlineStr) {
      return {
        variant: 'default',
        text: t('typography.notAvailable'),
        icon: 'clock'
      }
    }

    const deadline = parseISO(deadlineStr)
    const now = new Date()

    if (isTomorrow(deadline)) {
      return {
        variant: 'warning',
        text: t('grants.myTasksTable.deadline.tomorrow'),
        icon: 'clock'
      }
    }

    if (isToday(deadline) && isBefore(now, deadline)) {
      return {
        variant: 'critical',
        text: t('grants.myTasksTable.deadline.today'),
        icon: 'clock'
      }
    }

    if (isAfter(now, deadline)) {
      return {
        variant: 'critical',
        text: t('grants.myTasksTable.deadline.overdue'),
        icon: 'alerts'
      }
    }

    return {
      variant: 'default',
      text: format(deadline, 'dd MMM, HH:mm'),
      icon: 'clock'
    }
  }

  const getTooltipContent = (deadlineStr: string | null): string | null => {
    if (!deadlineStr) {
      return null
    }

    const deadline = parseISO(deadlineStr)
    const now = new Date()

    if (!isToday(deadline) && !isTomorrow(deadline) && !isAfter(now, deadline)) {
      return null
    }

    const hoursDifference = Math.abs(differenceInHours(now, deadline))

    if (isAfter(now, deadline)) {
      if (hoursDifference < 24) {
        return t('grants.myTasksTable.deadline.hoursOverdue', { hours: hoursDifference })
      }

      const daysPassed = Math.floor(hoursDifference / 24)

      return t('grants.myTasksTable.deadline.daysOverdue', { count: daysPassed })
    }

    return t('grants.myTasksTable.deadline.hoursLeft', { hours: hoursDifference })
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <TableProvider
      columns={columnsTasks}
      totalResults={totalResults}
      isLoading={isLoading}
      handleRequestSort={handleRequestSort}
      sortDirection={sortDirection}
      sortByColumn={sortByColumn}
      illustration="no-tasks"
      emptyTableTitle={t('grants.myTasksTable.emptyTable.title')}
      emptyTableDescription={t('grants.myTasksTable.emptyTable.description')}
    >
      {totalResults != null && totalResults > 0 && filters}
      <Table>
        <TableBody>
          {data?.map(task => {
            const { id, dueByDate, form, candidate, program } = task
            const { id: candidateId, name: candidateName, countryCode } = candidate
            const { id: programId, name: programName } = program
            const { formId, displayName: formDisplayName } = form || {}
            const { variant, text, icon } = getDeadlineBadgeProps(dueByDate)
            const tooltipContent = getTooltipContent(dueByDate)

            return (
              <TableRow
                key={`grants-task-${id}`}
                data-testid={`grants-task-${id}`}
                onClick={() => navigateToTask(programId, programName, candidateId, formId)}
              >
                <TableCell inlineStyle={{ width: '150px' }}>
                  <Flex align="center">
                    <Tooltip content={getCountryNameFromAlpha3Code(countryCode)} placement="right">
                      <div className={styles.tooltipContent}>
                        <FlagAvatar code3={countryCode} width="28px" />
                      </div>
                    </Tooltip>
                  </Flex>
                </TableCell>
                <TableCell>{candidateName}</TableCell>
                <TableCell>{programName}</TableCell>
                <TableCell>
                  <Badge variant="default">{formDisplayName}</Badge>
                </TableCell>
                <TableCell>
                  {tooltipContent ? (
                    <Tooltip content={tooltipContent}>
                      <div>
                        <Badge variant={variant} icon={icon}>
                          {text}
                        </Badge>
                      </div>
                    </Tooltip>
                  ) : (
                    <Badge variant={variant} icon={icon}>
                      {text}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </Flex>
    </TableProvider>
  )
}
